import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router';
import { useIntl } from 'react-intl';
import { useTheme } from '@emotion/react';
import { _routes as routes } from '@/core/router/routes';
import { Routes } from '@/core/router/routes.types';
import { getUserToken } from '@/redux/user';
import { useGetBrandRestaurantsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { useGetRestaurantShiftsQuery } from '@/services/innovorder/restaurant/restaurant.endpoints';
import { useDeleteShiftMutation } from '@/services/innovorder/shift/shift.endpoints';
import { RestaurantShift } from '@/services/innovorder/restaurant/restaurant.type';
import { getShiftsTableColumnsVM, getShiftsTableRowsVM } from './utils';

export const useShiftsViewModel = () => {
    const intl = useIntl();
    const theme = useTheme();
    const history = useHistory();
    const { brandId } = useParams<{ brandId: string }>();
    const token = useSelector(getUserToken);

    const tabRoutes = useMemo(() => routes.filter(({ tab }) => tab === 'brands'), []);

    const { data: restaurants = [], isLoading: isLoadingRestaurants } = useGetBrandRestaurantsQuery(
        { brandId: parseInt(brandId, 10), token },
        { refetchOnMountOrArgChange: true },
    );

    const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);

    useEffect(() => {
        if (selectedRestaurantId === null && restaurants.length > 0) {
            setSelectedRestaurantId(restaurants[0].restaurantId);
        }
    }, [restaurants, selectedRestaurantId]);

    const restaurantOptions = useMemo(
        () => restaurants.map((restaurant) => ({ value: restaurant.restaurantId, label: restaurant.name })),
        [restaurants],
    );

    const { data: shifts, isLoading: isLoadingShifts } = useGetRestaurantShiftsQuery(
        { restaurantId: String(selectedRestaurantId), token },
        { skip: selectedRestaurantId === null, refetchOnMountOrArgChange: true },
    );

    const [deleteShift, { isSuccess: isDeleteSuccess, isLoading: isLoadingDelete }] = useDeleteShiftMutation();

    const [shiftToDelete, setShiftToDelete] = useState<RestaurantShift | null>(null);

    useEffect(() => {
        if (isDeleteSuccess) {
            setShiftToDelete(null);
        }
    }, [isDeleteSuccess]);

    const handleOpenDeleteModal = useCallback((shift: RestaurantShift) => {
        setShiftToDelete(shift);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setShiftToDelete(null);
    }, []);

    const handleDeleteShift = useCallback(() => {
        if (shiftToDelete && !shiftToDelete.isDefault) {
            deleteShift({ shiftId: shiftToDelete.shiftId, token });
        }
    }, [deleteShift, shiftToDelete, token]);

    const handleAddShift = useCallback(() => {
        history.push(generatePath(Routes.CreateShift, { brandId }));
    }, [history, brandId]);

    const handleSelectRestaurant = useCallback((restaurantId: number) => {
        setSelectedRestaurantId(restaurantId);
    }, []);

    const rows = useMemo(
        () => getShiftsTableRowsVM(shifts, handleOpenDeleteModal, intl, theme, brandId),
        [shifts, handleOpenDeleteModal, intl, theme, brandId],
    );
    const columns = useMemo(() => getShiftsTableColumnsVM(intl), [intl]);

    return {
        tabRoutes,
        rows,
        columns,
        isLoading: isLoadingRestaurants || (selectedRestaurantId !== null && isLoadingShifts),
        hasRestaurants: restaurants.length > 0,
        restaurantOptions,
        selectedRestaurantId,
        shiftToDelete,
        isLoadingDelete,
        handleAddShift,
        handleSelectRestaurant,
        handleCloseDeleteModal,
        handleDeleteShift,
    };
};
